import { Group, Stack } from "@mantine/core";
import { ListOfRecommendedPlaylists } from "../playlists/RecommendPlaylists";
import { useHasRecommendedPlaylists } from "@/store/selectors/playlistSelectors";
import { UserProfileNoResults } from "./UserProfileNoResults";
import { Button } from "../ui/Button";
import { IconRefresh } from "@tabler/icons-react";
import {
  useAllowPlaylistRecs,
  useIsRequestingRecommendedPlaylists,
  useUserProfileData,
} from "@/store/selectors/userProfileSelectors";
import { useStore } from "@/store";
import { RecommendationService } from "@/services/RecommendationService";
import { useMemo, useCallback } from "react";
import { useActiveUser } from "@/store/selectors";
import { Analytics } from "@/services/Analytics";

export function UserProfileRecommendedPlaylists(props: {
  onClick: () => void;
}) {
  const { onClick } = props;
  const hasRecommendedPlaylists = useHasRecommendedPlaylists();
  const userProfileData = useUserProfileData();
  const userCustomPlaylistRecommendationRequest = useStore(
    (state) => state.playlists.userCustomPlaylistRecommendationRequest,
  );
  const { allowPlaylistRecs } = useAllowPlaylistRecs();
  const isRequestingRecommendedPlaylists =
    useIsRequestingRecommendedPlaylists();

  const activeUser = useActiveUser();

  const buttonText = useMemo(() => {
    if (isRequestingRecommendedPlaylists) {
      return "Getting new playlist recommendations...";
    }
    return hasRecommendedPlaylists
      ? "Get new playlist recommendations"
      : "Get playlist recommendations";
  }, [isRequestingRecommendedPlaylists, hasRecommendedPlaylists]);

  const handleRefreshClick = useCallback(
    (event: any) => {
      Analytics.click({
        context: "User profile",
        action: "Get recommended playlists button",
        label: buttonText,
        event,
      });

      async function refreshRecommendedPlaylists() {
        try {
          await RecommendationService.getRecommendedPlaylists({
            userProfileData,
            customRequest: userCustomPlaylistRecommendationRequest,
            includeAllUserProfileData: allowPlaylistRecs,
            userId: activeUser?.id || "",
          });
        } catch (error) {
          console.error(
            "Error refreshing recommended playlists",
            error,
          );
        }
      }
      refreshRecommendedPlaylists();
      onClick();
    },
    [
      buttonText,
      userProfileData,
      userCustomPlaylistRecommendationRequest,
      allowPlaylistRecs,
      activeUser?.id,
      onClick,
    ],
  );

  const MainContent = useMemo(() => {
    if (isRequestingRecommendedPlaylists) {
      return null;
    }

    if (!hasRecommendedPlaylists && !allowPlaylistRecs) {
      return (
        <UserProfileNoResults
          title="You don't have any recommended playlists yet."
          buttonText={
            allowPlaylistRecs ? undefined : "Get recommended playlists"
          }
          href={allowPlaylistRecs ? undefined : "/?step=select-subjects"}
        />
      );
    }

    return <ListOfRecommendedPlaylists />;
  }, [
    allowPlaylistRecs,
    hasRecommendedPlaylists,
    isRequestingRecommendedPlaylists,
  ]);

  return (
    <Stack maw={800}>
      {MainContent}

      {allowPlaylistRecs && (
        <Group
          align="center"
          justify="flex-start"
          gap="xs"
          mt={hasRecommendedPlaylists ? "md" : undefined}
        >
          <Button
            radius="xl"
            size="sm"
            variant="light"
            color="slate"
            leftSection={
              <IconRefresh
                size={16}
                className={
                  isRequestingRecommendedPlaylists ? "spin" : undefined
                }
              />
            }
            disabled={isRequestingRecommendedPlaylists}
            onClick={handleRefreshClick}
          >
            {buttonText}
          </Button>
        </Group>
      )}
    </Stack>
  );
}
